import React from 'react';
import { Pressable, Text, View } from 'react-native';
import {
  MENU_FILTER_TAG_VOCAB,
  PRODUCT_ALLERGEN_VOCAB,
  normalizeProductTagKey,
  pickMenuFilterTagsFromUsage,
  usageTagsFromCsv,
  usageTagsToCsv,
  mergeUsageTags,
  toggleTagInList,
} from '@shared/menuFilters';
import { ticketColors } from '../../shared/theme/ticketColors';
import { catalogProductFormStyles as styles } from './catalogProductForm.styles';
import type { CatalogProductFormFieldsProps } from './catalogProductFormFields.types';

type Props = Pick<
  CatalogProductFormFieldsProps,
  'draft' | 'onAllergensChange' | 'onUsageTagsChange' | 'mobile' | 'desktopRail'
>;

function ChipRow({
  options,
  selected,
  onToggle,
}: {
  options: Array<{ key: string; label: string }>;
  selected: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
      {options.map((opt) => {
        const on = selected.has(opt.key);
        return (
          <Pressable
            key={opt.key}
            onPress={() => onToggle(opt.key)}
            accessibilityRole="button"
            accessibilityState={{ selected: on }}
            style={{
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: on ? ticketColors.brandPrimary : ticketColors.rule,
              backgroundColor: on ? ticketColors.brandPrimary : ticketColors.paper,
            }}
          >
            <Text
              style={{
                fontSize: 12,
                fontWeight: '700',
                color: on ? ticketColors.goldInk : ticketColors.ink2,
              }}
            >
              {opt.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/**
 * Alérgenos (contiene → guest “Sin …”) + tags de carta (usage_tags include).
 * Portable: mismo campo usage_tags que farmacia; vocab de carta es subset filtrable.
 */
export function CatalogProductFormMenuFilterFields({
  draft,
  onAllergensChange,
  onUsageTagsChange,
  desktopRail = false,
}: Props) {
  const allergenSelected = new Set(
    (draft.allergens ?? []).map(normalizeProductTagKey).filter(Boolean),
  );
  const menuSelected = new Set(
    pickMenuFilterTagsFromUsage(usageTagsFromCsv(draft.usageTags ?? '')),
  );

  const toggleAllergen = (key: string) => {
    onAllergensChange?.(toggleTagInList(draft.allergens ?? [], key));
  };

  const toggleMenuTag = (key: string) => {
    const nextMenu = toggleTagInList([...menuSelected], key);
    const merged = mergeUsageTags(draft.usageTags ?? '', nextMenu);
    onUsageTagsChange?.(usageTagsToCsv(merged));
  };

  return (
    <View style={styles.ticketFormSection} testID="catalog.form.menu-filters">
      {!desktopRail ? <Text style={styles.ticketFormSectionTitle}>Filtros de carta</Text> : null}

      <View style={styles.ticketFormField}>
        <Text style={styles.ticketFormLabel}>Contiene (alérgenos)</Text>
        <Text style={[styles.ticketFormLabel, { fontSize: 11, opacity: 0.7, fontWeight: '400' }]}>
          El cliente puede filtrar “Sin …” en la carta QR.
        </Text>
        <ChipRow
          options={PRODUCT_ALLERGEN_VOCAB}
          selected={allergenSelected}
          onToggle={toggleAllergen}
        />
      </View>

      <View style={styles.ticketFormField}>
        <Text style={styles.ticketFormLabel}>Este plato es…</Text>
        <Text style={[styles.ticketFormLabel, { fontSize: 11, opacity: 0.7, fontWeight: '400' }]}>
          Gustos en la carta QR. El cliente elige “Quiero vegano / carne…”.
        </Text>
        <ChipRow
          options={MENU_FILTER_TAG_VOCAB}
          selected={menuSelected}
          onToggle={toggleMenuTag}
        />
      </View>
    </View>
  );
}
